import { BUDGET_TRUSTWORTHINESS_THRESHOLD } from '@/config';
import { KEY_EVENT_KEYS } from '@/config/constants';
import { useConfigContext } from '@/context/configContext';
import {
    Can,
    FEATURE_FLAG_ACTIONS,
    FEATURE_FLAG_SUBJECTS,
} from '@/features/Casl';
import { cn } from '@/utils';
import { CurrencyInput, Typography, Tooltip } from '@analytical-alley/ui';
import { useEffect } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { BudgetSlider } from './BudgetSlider';
import { BudgetTooltip } from './BudgetTooltip';
import { DisableToggle } from './DisableToggle';
import { LockToggle } from './LockToggle';
import { validateMediaBudgetInput } from './validateMediaBudgetInput';

export interface BudgetRowValues {
    investment: number | null;
    locked: boolean;
    excluded?: boolean;
}

export interface SliderConfig {
    min: number;
    minRecommended: number;
    maxRecommended: number;
    recommendedBudget: number;
    max: number;
    investmentStep?: number;
}

interface Props {
    active?: boolean;
    name: string;
    title?: string;
    formValues: BudgetRowValues;
    sliderConfig: SliderConfig;
    onSubmit: (key: string, data: BudgetRowValues) => void;
    editPermissionFeatureFlag: FEATURE_FLAG_SUBJECTS;
    onOutOfBounds?: (investment: number | null) => void;
    modalResetKey?: number;
    disabled?: boolean;
}

export const ModifyBudgetRow = ({
    name,
    title,
    sliderConfig,
    formValues,
    onSubmit,
    active,
    editPermissionFeatureFlag,
    disabled = false,
}: Props) => {
    const { locale, currency } = useConfigContext();
    const methods = useForm<BudgetRowValues>({
        defaultValues: formValues,
        values: formValues,
    });

    const {
        reset,
        formState: { isDirty },
    } = methods;

    useEffect(() => {
        reset(formValues);
    }, [formValues, reset]);

    const submit = (force?: boolean) => {
        if (isDirty || force) {
            methods.handleSubmit((data) => onSubmit(name, data))();
        }
    };

    const { watch, control } = methods;
    const investmentValue = watch('investment');
    const isLocked = watch('locked');
    const isExcluded = watch('excluded') ?? false;

    const isRowDisabled = disabled || isExcluded;

    const { minRecommended, maxRecommended } = sliderConfig;

    const threshold = 1 + BUDGET_TRUSTWORTHINESS_THRESHOLD;

    const validateBudget = () => {
        if (
            !isLocked ||
            !investmentValue ||
            investmentValue === 0 ||
            !minRecommended ||
            !maxRecommended
        ) {
            return { isOutOfRange: false, isOutOfBounds: false };
        }

        const lowerBound = minRecommended / threshold;
        const upperBound = maxRecommended * threshold;

        const isOutOfBounds =
            investmentValue < lowerBound || investmentValue > upperBound;
        const isOutOfRange =
            investmentValue < minRecommended ||
            investmentValue > maxRecommended;

        return { isOutOfRange, isOutOfBounds };
    };

    const { isOutOfRange } = validateBudget();

    return (
        <div
            className={cn(
                'flex gap-4 items-center px-4 py-1.5 w-full rounded-xl max-md:flex-wrap max-md:max-w-full flex-wrap transition-all duration-200 hover:bg-white/5 group/row',
                {
                    'bg-white/10': active,
                    'border border-error/50 bg-error/10': isOutOfRange,
                    'opacity-50 grayscale': isExcluded,
                },
            )}
        >
            <Typography
                ellipsisTooltip={
                    <div className="rounded-md bg-white text-dark text-sm p-3">
                        {title || name}
                    </div>
                }
                className="text-nowrap self-stretch my-auto text-sm font-medium leading-5 w-[20%] text-white/70 group-hover/row:text-white transition-colors"
            >
                {title || name}
            </Typography>

            <Controller
                control={control}
                name="investment"
                render={({ field: { onChange, ...fieldProps } }) => (
                    <Tooltip>
                        <Tooltip.Trigger>
                            <CurrencyInput
                                {...fieldProps}
                                disabled={!isLocked || isRowDisabled}
                                value={
                                    investmentValue == null
                                        ? ''
                                        : investmentValue
                                }
                                intlConfig={{ locale, currency }}
                                className={cn(
                                    'w-32 text-nowrap disabled:opacity-50 justify-center px-4 py-1.5 text-sm leading-5 rounded-md shadow-lg backdrop-blur-[15px] bg-white/5 glass text-dark dark:text-white border border-white/10 focus-visible:outline-hidden transition-all',
                                    {
                                        'cursor-not-allowed':
                                            !isLocked || isRowDisabled,
                                    },
                                )}
                                decimalScale={0}
                                onBlur={() => submit()}
                                onKeyDown={(e) => {
                                    if (e.key === KEY_EVENT_KEYS.ENTER) {
                                        submit();
                                    }
                                }}
                                groupSeparator={' '}
                                onValueChange={(_, __, values) => {
                                    const value = values?.float;
                                    onChange(validateMediaBudgetInput(value));
                                }}
                            />
                        </Tooltip.Trigger>
                        <Tooltip.Content className="tooltip-content">
                            Got a fixed budget for this channel? Lock the budget -{'>'}, then input the number here. We will optimise other values and keep this one constant.
                        </Tooltip.Content>
                    </Tooltip>
                )}
            />

            <div className="grow">
                <div className="flex min-w-7 gap-4">
                    <BudgetSlider
                        value={investmentValue || 0}
                        config={{
                            ...sliderConfig,
                            recommendedBudget: isLocked
                                ? (investmentValue ??
                                  sliderConfig.recommendedBudget)
                                : sliderConfig.recommendedBudget,
                        }}
                        tooltipContent={({ hoveredValue }) => (
                            <BudgetTooltip
                                hoveredValue={hoveredValue}
                                variableName={title || name}
                            />
                        )}
                        variable={name}
                    />

                    <Can
                        I={FEATURE_FLAG_ACTIONS.EDIT}
                        a={editPermissionFeatureFlag}
                    >
                        <LockToggle
                            name={name}
                            control={control}
                            isLocked={isLocked}
                            disabled={isRowDisabled}
                            onUnlock={() => {
                                methods.setValue(
                                    'investment',
                                    sliderConfig.recommendedBudget,
                                    { shouldDirty: true },
                                );
                                methods.handleSubmit((data) =>
                                    onSubmit(name, data),
                                )();
                            }}
                            onLock={() => submit(true)}
                        />
                        <DisableToggle
                            name={name}
                            control={control}
                            isExcluded={isExcluded}
                            disabled={disabled}
                            onExclude={() => {
                                methods.setValue('investment', 0, {
                                    shouldDirty: true,
                                });
                                methods.setValue('locked', true, {
                                    shouldDirty: true,
                                });
                                methods.handleSubmit((data) =>
                                    onSubmit(name, data),
                                )();
                            }}
                            onInclude={() => {
                                methods.setValue(
                                    'investment',
                                    sliderConfig.recommendedBudget,
                                    { shouldDirty: true },
                                );
                                methods.setValue('locked', false, {
                                    shouldDirty: true,
                                });
                                methods.handleSubmit((data) =>
                                    onSubmit(name, data),
                                )();
                            }}
                        />
                    </Can>
                </div>
            </div>

            {isOutOfRange && (
                <div className="w-full mt-2">
                    <Typography className="text-error text-xs font-semibold">
                        Warning: The selected budget is outside the recommended
                        budget range.
                    </Typography>
                </div>
            )}
        </div>
    );
};
